/**
 * Атрибуция переходов с сайта в сторы (клиентский модуль).
 *
 * - UTM / клик-идентификаторы с посадочной сохраняются в localStorage на 30 дней
 *   (first-touch и last-touch), чтобы не терялись при переходах по сайту.
 * - Ссылка в Google Play получает `referrer=utm_source=…&utm_campaign=…` — приложение
 *   читает его через Install Referrer API и отправляет при регистрации.
 * - Ссылка в App Store получает `ct` (campaign token) и `pt` (provider token, если задан) —
 *   видно в App Store Connect → App Analytics → Campaigns.
 * - Смартлинк AppMetrica получает utm-параметры — атрибуция в трекере.
 */

import { SETTINGS_GENERAL } from '@/config/settings.general'

export const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
] as const
const CLICK_ID_KEYS = ['yclid', 'gclid', 'vkclid'] as const

const STORAGE_FIRST = 'bk_utm_first'
const STORAGE_LAST = 'bk_utm_last'
const TTL_MS = 30 * 24 * 60 * 60 * 1000

export type StoredUtm = {
    params: Record<string, string>
    at: number
    landing: string
}

const readStorage = (key: string): StoredUtm | null => {
    try {
        const raw = window.localStorage.getItem(key)
        if (!raw) return null
        const parsed = JSON.parse(raw) as StoredUtm
        return Date.now() - parsed.at < TTL_MS ? parsed : null
    } catch {
        return null
    }
}

const writeStorage = (key: string, value: StoredUtm): void => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // приватный режим / запрет хранилища — атрибуция просто не сохранится
    }
}

/** Сохранить utm текущей страницы (если есть). Вызывается на каждой загрузке сайта. */
export function captureLandingUtm(): void {
    const search = new URLSearchParams(window.location.search)
    const params: Record<string, string> = {}
    for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
        const value = search.get(key)?.trim()
        if (value) params[key] = value.slice(0, 200)
    }
    if (!params.utm_source && !CLICK_ID_KEYS.some(key => params[key])) return

    // Клик из Директа без явного utm_source — всё равно Яндекс
    if (!params.utm_source && params.yclid) params.utm_source = 'yandex'

    const entry: StoredUtm = {
        params,
        at: Date.now(),
        landing: window.location.pathname,
    }
    if (!readStorage(STORAGE_FIRST)) writeStorage(STORAGE_FIRST, entry)
    writeStorage(STORAGE_LAST, entry)
}

/** UTM для перехода в стор: из текущего URL, иначе последнее сохранённое касание */
export function getAttributionParams(): Record<string, string> {
    const search = new URLSearchParams(window.location.search)
    if (search.get('utm_source')) {
        return Object.fromEntries(
            UTM_KEYS.flatMap(key =>
                search.get(key) ? [[key, search.get(key)!]] : []
            )
        )
    }
    const stored = readStorage(STORAGE_LAST) ?? readStorage(STORAGE_FIRST)
    return stored?.params ?? {}
}

const isGooglePlay = (url: URL): boolean => url.hostname === 'play.google.com'
const isAppStore = (url: URL): boolean =>
    url.hostname === 'apps.apple.com' || url.hostname === 'itunes.apple.com'
const isSmartlink = (url: URL): boolean =>
    url.hostname.startsWith('redirect.appmetrica.yandex')

export const isStoreUrl = (href: string): boolean => {
    try {
        const url = new URL(href)
        return isGooglePlay(url) || isAppStore(url) || isSmartlink(url)
    } catch {
        return false
    }
}

/** Ссылка в стор / смартлинк с атрибуцией. Без utm возвращает исходную ссылку. */
export function withStoreAttribution(
    href: string,
    params: Record<string, string> = getAttributionParams()
): string {
    if (!params.utm_source) return href
    let url: URL
    try {
        url = new URL(href)
    } catch {
        return href
    }

    const utm = new URLSearchParams(
        UTM_KEYS.flatMap(key => (params[key] ? [[key, params[key]]] : []))
    )

    if (isGooglePlay(url)) {
        url.searchParams.set('referrer', utm.toString())
    } else if (isAppStore(url)) {
        // ct — до 40 символов, видно в App Store Connect; pt — токен провайдера аккаунта
        const campaign = [params.utm_source, params.utm_campaign]
            .filter(Boolean)
            .join('_')
        url.searchParams.set('ct', campaign.slice(0, 40))
        if (SETTINGS_GENERAL.APP_STORE_PROVIDER_TOKEN) {
            url.searchParams.set(
                'pt',
                SETTINGS_GENERAL.APP_STORE_PROVIDER_TOKEN
            )
            url.searchParams.set('mt', '8')
        }
    } else if (isSmartlink(url)) {
        for (const [key, value] of utm) url.searchParams.set(key, value)
        for (const key of CLICK_ID_KEYS) {
            if (params[key]) url.searchParams.set(key, params[key])
        }
    }
    return url.toString()
}
